import { DefaultApi } from '@sendhub/integrations/api-client-typescript-axios'
import axios, { AxiosRequestConfig, isAxiosError } from 'axios'
import Cookies from 'js-cookie'
import { toast } from 'react-toastify'
import { INTEGRATIONS_BASE_URL } from '../../utils/config'
import { attachSendhubSessionKey } from '../../utils/session-key'
import { REFRESH_TOKEN_HEADER, attachBearerToken, clearTokens, getRefreshToken, setTokens } from '../../utils/token-store'

export function createIntegrationsApiClient() {
  let refreshPromise: Promise<void> | null = null
  const MAX_REFRESH_TOKEN_RETRIES = 1
  const MAX_TRANSIENT_REFRESH_RETRIES = 3
  const REFRESH_RETRY_TOAST_ID = 'auth-refresh-retry'

  function clearRefreshRetryToast() {
    toast.dismiss(REFRESH_RETRY_TOAST_ID)
  }

  function getRetryDelayMs(attempt: number): number {
    const exponentialDelayMs = 3000 * 2 ** (attempt - 1)
    const jitterMs = Math.floor(Math.random() * 2000)
    return exponentialDelayMs + jitterMs
  }

  function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  function showRefreshRetryToast(attempt: number, delayMs: number) {
    const retryInSeconds = Math.ceil(delayMs / 1000)
    const message = `Network issue refreshing your session. Retrying in ${retryInSeconds}s (${attempt}/${MAX_TRANSIENT_REFRESH_RETRIES}).`

    if (toast.isActive(REFRESH_RETRY_TOAST_ID)) {
      toast.update(REFRESH_RETRY_TOAST_ID, {
        render: message,
        type: 'info',
        autoClose: false,
        closeButton: true,
      })
      return
    }

    toast.info(message, {
      toastId: REFRESH_RETRY_TOAST_ID,
      autoClose: false,
      closeOnClick: false,
      pauseOnHover: true,
    })
  }

  function forceUnauthenticated() {
    clearTokens()

    // The cookie is set with domain '.sendhub.com' in prod (host-only on
    // localhost). Cookies.remove only deletes it when the domain attribute
    // matches what it was set with.
    if (window.location.hostname === 'localhost') {
      Cookies.remove('refreshToken')
    } else {
      Cookies.remove('refreshToken', { domain: '.sendhub.com' })
    }

    // TODO: replace this bridge with a first-class Redux action in a separate cleanup task.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const authActions = window?.authActions
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const dispatch = window?.dispatch
    const setLoggedOutAction = authActions?.setIsLoggedIn?.(false)
    if (dispatch && setLoggedOutAction) {
      dispatch(setLoggedOutAction)
      return
    }

    if (window.location.pathname !== '/signin') {
      window.location.assign('/signin')
    }
  }

  function isTransientRefreshError(error: unknown): boolean {
    if (!isAxiosError(error)) return false
    if (error.response) return false

    return ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error.code || '')
  }

  async function refreshWithRetry(): Promise<void> {
    for (let attempt = 1; attempt <= MAX_TRANSIENT_REFRESH_RETRIES; attempt++) {
      try {
        await integrationsApi.refreshToken()
        clearRefreshRetryToast()
        return
      } catch (reason) {
        if (isAxiosError(reason) && reason.response?.status === 401) {
          clearRefreshRetryToast()
          forceUnauthenticated()
          throw reason
        }

        const shouldRetry = isTransientRefreshError(reason) && attempt < MAX_TRANSIENT_REFRESH_RETRIES
        if (!shouldRetry) {
          clearRefreshRetryToast()
          forceUnauthenticated()
          throw reason
        }

        const delayMs = getRetryDelayMs(attempt)
        showRefreshRetryToast(attempt, delayMs)
        await sleep(delayMs)
      }
    }
  }

  function getOrCreateRefreshPromise(): Promise<void> {
    if (!refreshPromise) {
      refreshPromise = refreshWithRetry().finally(() => {
        refreshPromise = null
      })
    }

    return refreshPromise
  }

  const axiosInstance = axios.create({ withCredentials: true })

  // /auth/sso authenticates from the SendHub session, which can't reach this
  // origin as a cookie — forward it as a header instead. See utils/session-key.ts.
  attachSendhubSessionKey(axiosInstance)

  // The JWT pair likewise can't be relied on as cookies cross-site. See
  // utils/token-store.ts.
  attachBearerToken(axiosInstance)

  // The refresh token authenticates /auth/refresh itself, so it travels in its
  // own header rather than as the Authorization bearer.
  axiosInstance.interceptors.request.use((requestConfig) => {
    const heldRefreshToken = getRefreshToken()
    if (heldRefreshToken && requestConfig.url?.includes('auth/refresh')) requestConfig.headers.set(REFRESH_TOKEN_HEADER, heldRefreshToken)
    return requestConfig
  })

  // Both endpoints return the pair in their body — capture it centrally so
  // every caller benefits, not just the one that happened to trigger it.
  axiosInstance.interceptors.response.use((response) => {
    const url = response.config?.url || ''
    if (url.includes('auth/sso') || url.includes('auth/refresh')) setTokens(response.data || {})
    return response
  })

  axiosInstance.interceptors.response.use(null, refreshTokenOn401Interceptor)

  async function refreshTokenOn401Interceptor(error: any): Promise<any> {
    if (isAxiosError(error) && error.response?.status === 403) {
      const message =
        (error.response.data && (error.response.data.message as string)) || 'This is not allowed. Please get the access from your admin.'
      toast.error(message)
      throw error
    }
    if (!isAxiosError(error) || error.response?.status !== 401) throw error

    // Held token first: the cookie is unreadable from JS wherever it was issued
    // `Secure` to a plain-HTTP page, which made this gate always fail and turned
    // every 401 into another SSO round instead of a refresh.
    const hasRefreshToken = !!getRefreshToken() || !!Cookies.get('refreshToken')
    if (!hasRefreshToken) {
      forceUnauthenticated()
      throw error
    }

    // A 401 from the refresh endpoint means the session is no longer recoverable.
    if (error?.config?.url?.includes('auth/refresh')) {
      forceUnauthenticated()
      throw error
    }

    const originalRequest = error.config as AxiosRequestConfig & {
      refreshTokenRetries?: number
    }

    if ((originalRequest.refreshTokenRetries || 0) >= MAX_REFRESH_TOKEN_RETRIES) throw error
    originalRequest.refreshTokenRetries = (originalRequest.refreshTokenRetries || 0) + 1

    await getOrCreateRefreshPromise()

    return axiosInstance(originalRequest)
  }
  return new DefaultApi(undefined, `${INTEGRATIONS_BASE_URL}/api/v2`, axiosInstance)
}

export const integrationsApi = createIntegrationsApiClient()
